import { useEffect, useRef, type Dispatch, type ReactNode } from 'react';
import type { TariffFetchState } from '../fares/fare_expansion.js';
import type { FareSel } from '../fares/fare_selection.js';
import type { FlightTariffSource } from '../fares/fare_source.js';
import { FlightFaresDialog } from '../fares/fares_dialog.js';
import { FlightRequestFaresPage, type FaresRequestText } from '../fares/fares_request_page.js';
import { carrierNameOf } from '../results/flight_offer_source.js';
import { useExplorerEnv, type FlightExplorerEnv } from './explorer_env.js';
import type { FlightExplorerOffer } from './search_source.js';
import { policyCeiling } from '../results/filter_logic.js';
import type { OfferFares } from '../results/offer/offer_list.js';
import {
  activeOffers,
  faresLegOf,
  isFaresStage,
  isFinalPick,
  type ExplorerEvent,
  type ExplorerFares,
  type ExplorerPick,
  type ExplorerState,
} from './explorer_state.js';
import { activeOffersOfLeg, faresFlightOf } from './explorer_steps.js';

/**
 * **A fase de tarifa do explorer** — a tela "Escolha as tarifas de cada voo"
 * no lugar da lista (#2262), para TODO modo desde a padronização de 30/09
 * (#3875): o ida e volta com as duas pernas, só ida e multitrecho com a perna
 * ativa. Componente à parte porque o `FlightExplorer` vive no teto de
 * complexidade/linhas — e porque tudo aqui é derivação do MESMO estado, sem um
 * `useState` próprio: o tariff está no `useOfferFares` do host, os voos no
 * `chosen`, e o único gesto que sai daqui é o `takePick` que a escada já emitia.
 *
 * **Duas cascas, uma tela.** No desktop a página ocupa a coluna de resultados
 * (quem a monta é o `FlightExplorer`). No CELULAR (`phone`) ela abre na casca de
 * tela cheia da conversa (`FlightFaresDialog`): cabeçalho com voltar, respiro
 * lateral e CTA fixo no pé — a mesma tela que o card do chat abre, sem o chrome
 * do explorer por cima. O voltar da casca é o "Alterar voo" da perna que trouxe
 * a pessoa até aqui (`pick_removed`), então a lista que reaparece é a dela.
 *
 * Rende `null` quando um dos picks não resolve contra a lista viva (a busca
 * pousou por cima e podou a oferta): aí o reducer já vai ter derrubado o pick,
 * e a lista volta sozinha no render seguinte.
 */
export function ExplorerFaresStage({
  state,
  fares,
  frozen,
  draftLock,
  confirming,
  ctaLabel,
  phone,
  send,
  onRetry,
  takePick,
}: Readonly<{
  state: ExplorerState;
  fares: OfferFares<FlightExplorerOffer>;
  frozen: boolean;
  /** Rascunho vivo na faixa/pill: o CTA trava — a tela nunca confirma um pedido
   *  que ela mesma não está mostrando (a mesma régua do `takePickFor`). */
  draftLock: boolean;
  /** O host está PROCESSANDO o confirm que saiu daqui (a bancada cotando a
   *  seleção antes do checkout): o CTA gira no lugar em que foi clicado. Fora
   *  disso, congelada (`frozen`) a tela do celular NÃO monta — a casca de tela
   *  cheia cobriria a faixa do host (o erro da cotação, com "Tentar novamente"
   *  e "Nova busca"), e a lista atrás já mostra a linha marcada. No desktop a
   *  página fica na coluna, `readOnly`, com a faixa do host visível acima. */
  confirming: boolean | undefined;
  /** O verbo do host no pick FINAL — ver `FlightExplorerProps.faresCtaLabel`.
   *  Num trecho intermediário do roteiro o CTA diz "Próximo": o pick não fecha
   *  nada, só avança o stepper. */
  ctaLabel: string;
  phone: boolean;
  send: Dispatch<ExplorerEvent>;
  onRetry: () => void;
  takePick: (pick: Required<ExplorerPick>) => void;
}>) {
  const env = useExplorerEnv();
  const roundTrip = state.request.mode === 'round_trip';
  const leg = faresLegOf(state);
  const outbound = faresFlightOf(state, roundTrip ? 0 : leg);
  const inbound = roundTrip ? faresFlightOf(state, 1) : null;
  if (!outbound || (roundTrip && !inbound)) return null;
  const pickId = state.chosen[leg]?.offerId;
  if (pickId === undefined || stageHidden(phone, frozen, Boolean(confirming))) return null;
  const tstate = fares.stateOf(pickId);
  const ceiling = policyCeiling(activeOffers(state));
  const offerOf = () => activeOffersOfLeg(state, leg).find((o) => o.offerId === pickId);
  const changeFlight = (which: 0 | 1) => {
    // No ida e volta cada perna volta à SUA lista (a ida derruba a volta
    // junto); nos outros modos só há a perna ativa.
    if (!frozen) send({ t: 'pick_removed', leg: roundTrip ? which : leg });
  };
  const page = (
    <FlightRequestFaresPage
      request={state.request}
      text={faresTextOf(env)}
      airportSwitchOf={env.airportSwitchOf}
      renderDetail={env.renderDetail}
      outbound={outbound}
      {...(inbound ? { inbound } : {})}
      state={tstate}
      sel={fares.selOf(pickId)}
      {...(frozen ? { readOnly: true } : {})}
      {...(ceiling ? { ceiling } : {})}
      ctaLabel={isFinalPick(state) ? ctaLabel : env.copy.fares.next}
      ctaDisabled={draftLock}
      ctaBusy={Boolean(confirming)}
      onPick={(patch) => {
        fares.pick(pickId, patch);
      }}
      onChangeFlight={changeFlight}
      onCheckout={(picked) => {
        // O MESMO caminho do rodapé da escada: quando o pick é o último
        // pendente por construção (`isFinalPick`), este clique grava a tarifa
        // E confirma a viagem; num trecho intermediário só grava e avança.
        const offer = offerOf();
        if (!offer) return;
        const position = activeOffersOfLeg(state, leg).indexOf(offer) + 1;
        makeSelectFares(takePick)(offer, position, tstate, picked, fares.selOf(pickId));
      }}
      onRetry={() => {
        const offer = offerOf();
        if (offer) fares.fetch(offer);
      }}
      // Stale (#2332): a recuperação é re-rodar a busca da fase, que recria os
      // handles — o retry repetiria o mesmo 409 pra sempre.
      {...(frozen ? {} : { onResearch: onRetry })}
      {...(phone
        ? {
            onBack: () => {
              changeFlight(roundTrip ? 1 : 0);
            },
          }
        : {})}
    />
  );
  return (
    <StageShell
      phone={phone}
      onClose={() => {
        changeFlight(roundTrip ? 1 : 0);
      }}
    >
      <StageHints draftPending={draftLock} confirmStuck={state.confirmStuck} />
      {page}
    </StageShell>
  );
}

/** Congelada pelo host SEM estar processando o pick, a tela do celular não
 *  monta — ver a doc de `confirming`. No desktop a página fica na coluna. */
function stageHidden(phone: boolean, frozen: boolean, confirming: boolean): boolean {
  return phone && frozen && !confirming;
}

/** A casca da fase: a coluna no desktop, a tela cheia da conversa no celular. */
function StageShell({
  phone,
  onClose,
  children,
}: Readonly<{ phone: boolean; onClose: () => void; children: ReactNode }>) {
  const { copy } = useExplorerEnv();
  if (!phone) return <>{children}</>;
  return (
    <FlightFaresDialog label={copy.fares.titleShort} onClose={onClose}>
      {children}
    </FlightFaresDialog>
  );
}

/** Os textos da tela de tarifas por pedido, no ambiente do explorer. */
export function faresTextOf(env: FlightExplorerEnv): FaresRequestText {
  const { copy, classify, locale, places } = env;
  return { copy: copy.fares, request: copy.request, classify, locale, places };
}

/** Os avisos que a coluna mostrava ao lado do título e que a tela substitui:
 *  o rascunho da pill (o CTA travou por isso) e a moeda mista do pick final
 *  que não fechou. Sem eles a tela travaria muda. Mesmas classes e papéis dos
 *  hints da coluna (`ExplorerHints`). */
function StageHints({
  draftPending,
  confirmStuck,
}: Readonly<{ draftPending: boolean; confirmStuck: boolean }>) {
  const { copy } = useExplorerEnv();
  if (!draftPending && !confirmStuck) return null;
  return (
    <div className="fex-hints">
      {draftPending && (
        <span className="fex-draft-hint" role="note">
          {copy.page.draftPending}
        </span>
      )}
      {confirmStuck && (
        <span className="fex-stuck-hint" role="alert">
          {copy.page.confirmUnavailable}
        </span>
      )}
    </div>
  );
}

/** Os ids opacos da seleção corrente da escada — `null` enquanto incompleta
 *  (CTA da escada só libera completa; o null é o cinto pro TS, não um estado). */
function pickedFares(view: FlightTariffSource, sel: FareSel): ExplorerFares | null {
  if (view.bundled.length > 0) return sel.bundled ? { bundledFareId: sel.bundled } : null;
  if (!sel.outbound) return null;
  return { outboundFareId: sel.outbound, ...(sel.return ? { returnFareId: sel.return } : {}) };
}

/** O pick da tela de tarifas, normalizado — quem decide o que ele SIGNIFICA é
 *  o sink (a perna fecha, ou a viagem confirma). Fábrica (e não método do
 *  componente) porque o callback tem a assinatura de 5 args do `onSelectFares`
 *  do `OfferListBody`, que o explorer ainda repassa à lista. */
export function makeSelectFares(sink: (pick: Required<ExplorerPick>) => void) {
  return (
    offer: FlightExplorerOffer,
    position: number,
    tstate: TariffFetchState,
    picked: { total: number; currency: string; names: string },
    sel: FareSel,
  ) => {
    if (tstate.phase !== 'ready') return;
    const fares = pickedFares(tstate.view, sel);
    if (!fares) return;
    const s = offer.segments[0];
    sink({
      offerId: offer.offerId,
      fares,
      summary: {
        position,
        carrier: s ? carrierNameOf(s) : '',
        fareNames: picked.names,
        total: picked.total,
        currency: picked.currency,
      },
    });
  };
}

export type SelectFaresFn = ReturnType<typeof makeSelectFares>;

/** A tela de tarifas MONTADA — `null` fora da fase (`isFaresStage`). Fora do
 *  `FlightExplorer` pelo teto de linhas dele; a decisão de ONDE ela entra (a
 *  coluna, ou por cima da lista no celular) fica com quem a monta. */
export function faresStageNode(props: Parameters<typeof ExplorerFaresStage>[0]): ReactNode {
  return isFaresStage(props.state) ? <ExplorerFaresStage {...props} /> : null;
}

/** Conta ao host que a tela de tarifas está em cena (`onFaresStage`, #3989) e
 *  entrega a MESMA volta do "Alterar voo"/✕ da casca: no ida e volta a perna da
 *  volta cai (a ida segue valendo); nos outros modos, a perna ativa. `null` ao
 *  sair da fase e ao desmontar — a trilha do host recua ao passo da lista.
 *
 *  Congelada (`frozen`: a cotação que falhou, o checkout fechado) a volta não
 *  pode agir — quem degela é o host (`resumeSeq`). Ela fica PEDIDA e sai assim
 *  que o congelamento cai, se a tela de tarifas ainda estiver em cena: o ← da
 *  barra nunca é um botão morto (#4463). */
export function useFaresStageHost(
  state: ExplorerState,
  send: Dispatch<ExplorerEvent>,
  frozen: boolean,
  onFaresStage: ((back: (() => void) | null) => void) | undefined,
): void {
  const inStage = isFaresStage(state);
  const leg = state.request.mode === 'round_trip' ? 1 : inStage ? faresLegOf(state) : 0;
  const requested = useRef(false);
  useEffect(() => {
    if (!inStage) requested.current = false;
    if (!inStage || frozen || !requested.current) return;
    requested.current = false;
    send({ t: 'pick_removed', leg });
  }, [inStage, leg, frozen, send]);
  useEffect(() => {
    if (!onFaresStage) return undefined;
    onFaresStage(
      inStage
        ? () => {
            if (frozen) requested.current = true;
            else send({ t: 'pick_removed', leg });
          }
        : null,
    );
    return () => {
      onFaresStage(null);
    };
  }, [inStage, leg, frozen, send, onFaresStage]);
}
